import React, { useState } from 'react';
import MediaDialog from '../components/MediaDialog';
import MediaViewport from '../components/MediaViewport';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { SystemStory, MaturityExplorer } from '../components/VisualStories';
import styles from '../styles/pages/Training.module.css';

// ─── GLOSSARY OF TERMS & CONNECTED PRINCIPLES ───
const glossaryTerms = [
  {
    title: 'Foundation Model (LLM)',
    tag: 'Core Reasoning Engine',
    desc: 'A model generates responses from the context it receives. The surrounding application supplies project knowledge, tools, and any persistent memory.',
    relation: 'Connects to: <span>Repository Memory</span>',
  },
  {
    title: 'Repository Memory',
    tag: 'Project Reference',
    desc: 'Providing an AI with version-controlled project files such as AGENTS.md, schemas, architecture records, and guidelines. These files give the AI context it can refer to across sessions.',
    relation: 'Informs: <span>Autonomous Agents</span>',
  },
  {
    title: 'Autonomous Agent',
    tag: 'Multi-Step Work',
    desc: 'A model equipped with tools can plan, carry out, and check multi-step work instead of only answering questions in chat.',
    relation: 'Operates via: <span>MCP Connectors</span>',
  },
  {
    title: 'Model Context Protocol (MCP)',
    tag: 'Tool Connections',
    desc: 'An open standard that lets AI applications connect to tools and data sources through a shared interface. Each connection still needs appropriate access controls.',
    relation: 'Powers: <span>Agentic Tooling</span>',
  },
  {
    title: 'Context Window',
    tag: 'Working Context',
    desc: 'The information a model can consider in one interaction. Teams can make better use of this space by selecting relevant project files instead of adding large amounts of unindexed data.',
    relation: 'Loaded from: <span>Documents & Repos</span>',
  },
  {
    title: 'The Safe-Loop',
    tag: 'Review and Verification',
    desc: 'A workflow that sets a plan, limits the scope of changes, and checks the result before changes are merged. The review steps depend on the project and its tools.',
    relation: 'Enforces: <span>Claims Hygiene</span>',
  },
  {
    title: 'Claims Hygiene',
    tag: 'Evidence-Based Claims',
    desc: 'A practice of checking claims against available evidence and clearly marking what remains uncertain.',
    relation: 'Supports: <span>Reviewed Releases</span>',
  },
  {
    title: 'Multi-Agent Swarm',
    tag: 'Decentralized Squads',
    desc: 'Several agents working on bounded tasks with a shared plan. Clear ownership and review help teams coordinate changes and resolve conflicts.',
    relation: 'Orchestrated by: <span>Task Managers</span>',
  },
];

// ─── MONOCHROMATIC AI & ENGINEERING TOOLKIT ───
const aiEcosystemTools = [
  { name: 'Claude Code', role: 'Agent CLI & Terminal', icon: '/images/logos/anthropic.svg' },
  { name: 'Cursor', role: 'AI Code Editor', icon: '/images/logos/cursor.svg' },
  { name: 'ChatGPT / OpenAI', role: 'Frontier LLM & APIs', icon: '/images/logos/openai.svg' },
  { name: 'Google Gemini', role: 'Multimodal Frontier', icon: '/images/logos/gemini.svg' },
  { name: 'MCP', role: 'Model Context Protocol', icon: '/images/logos/mcp.svg' },
  { name: 'LangChain', role: 'Agent Orchestration', icon: '/images/logos/langchain.svg' },
  { name: 'GitHub Copilot', role: 'Repo & CI Workflows', icon: '/images/logos/github.svg' },
  { name: 'Perplexity', role: 'Live Search & Grounding', icon: '/images/logos/perplexity.svg' },
  { name: 'Databricks', role: 'Lakehouse & Mosaic AI', icon: '/images/logos/databricks.svg' },
  { name: 'Snowflake', role: 'Cloud Data Cloud', icon: '/images/logos/snowflake.svg' },
  { name: 'dbt', role: 'Analytics Engineering', icon: '/images/logos/dbt.svg' },
  { name: 'PostgreSQL', role: 'Relational & pgvector', icon: '/images/logos/postgresql.svg' },
  { name: 'SQL', role: 'Warehouse & Query Layer', icon: '/images/logos/sql.svg' },
  { name: 'Google Cloud', role: 'Cloud Run · BigQuery · Vertex', icon: '/images/logos/googlecloud.svg' },
  { name: 'TypeScript', role: 'Typed Systems & CLIs', icon: '/images/logos/typescript.svg' },
  { name: 'React', role: 'Web Interfaces', icon: '/images/logos/react.svg' },
  { name: 'Python', role: 'AI & Data Services', icon: '/images/logos/python.svg' },
  { name: 'FastAPI', role: 'High-Speed APIs', icon: '/images/logos/fastapi.svg' },
  { name: 'Node.js', role: 'Runtime & Tooling', icon: '/images/logos/nodejs.svg' },
  { name: 'Flutter', role: 'Cross-Platform Mobile', icon: '/images/logos/flutter.svg' },
  { name: 'Supabase', role: 'Auth, Postgres & Realtime', icon: '/images/logos/supabase.svg' },
  { name: 'Cloudflare', role: 'Edge Workers & Pages', icon: '/images/logos/cloudflare.svg' },
  { name: 'Docker', role: 'Container Runtime', icon: '/images/logos/docker.svg' },
  { name: 'Hugging Face', role: 'Open Weights & Evals', icon: '/images/logos/huggingface.svg' },
];

// ─── TWO OPERATIONAL PATHWAYS ───
const toolPaths = [
  {
    role: 'Full-Stack · Software Engineering · Agent Workflows',
    title: 'Cursor: The AI-Native Code Editor',
    img: '/images/training/cursor-app-actual.png',
    desc: 'Work with AI inside the editor: navigate a codebase, propose changes, and review the diff before accepting it.',
    bullets: [
      'Multi-file Composer for full-stack feature generation and complex refactors',
      'Inline visual diff reviews with one-click Accept or Revert controls',
      'Codebase indexing across repositories for context-aware suggestions',
      'Background agent loops executing tests and verifying changes in real time',
    ],
    toolTag: 'TOOL: CURSOR APP · AI-NATIVE CODE EDITOR',
  },
  {
    role: 'Terminal · Autonomous Squads · DevOps & Architecture',
    title: 'Claude Code: The Agentic Terminal CLI',
    img: '/images/training/claude-code-actual.png',
    desc: 'Work from the terminal: explore a repository, plan a change, connect tools, and run the checks that verify the result.',
    bullets: [
      'Autonomous terminal agent with full file-system and git awareness',
      'Direct Model Context Protocol (MCP) bridges to databases, tools & GitHub',
      'Interactive plan-and-execute cycles with Safe-Loop human review gates',
      'Automated test execution, lint checks, and pre-commit build verification',
    ],
    toolTag: 'TOOL: CLAUDE CODE CLI · AGENTIC TERMINAL',
  },
];

// ─── 4 ARCHITECTURAL REFERENCE BLUEPRINTS ───
const architectureDiagrams = [
  {
    id: 'ai-native-spectrum',
    title: 'The AI-Native Spectrum Matrix',
    img: '/images/training/ai-native-spectrum.jpg',
    desc: 'A four-stage reference showing ways teams can add AI to work, from individual use to coordinated agents.',
    bullets: [
      'Shows how responsibilities and tools can change across four stages.',
      'Describes steps from individual use to coordinated agent workflows.',
      'Offers examples of how teams can progress from prompts to tool-assisted tasks.',
      'Shows ways to connect tools, databases, and version-controlled code.',
    ],
  },
  {
    id: 'brain-memory-hands',
    title: 'Cognitive Triad: Brain · Memory · Hands',
    img: '/images/training/brain-memory-hands.svg',
    desc: 'A reference architecture that separates the model, project files, and tools an AI application can use.',
    bullets: [
      'The Brain: Foundation LLMs handle logic, synthesis, and planning without storing state in weights.',
      'The Memory: Git repositories (AGENTS.md, rules, schemas, ADRs) provide immutable, versioned enterprise truth.',
      'The Hands: Model Context Protocol (MCP) bridges shell execution, databases, and external SaaS APIs.',
      'Project files can give teams a consistent reference across sessions; results may still vary.',
    ],
  },
  {
    id: 'operational-visibility-flywheel',
    title: 'Making Workflows Visible',
    img: '/images/training/operational-visibility-flywheel.svg',
    desc: 'A four-step approach to documenting manual workarounds and deciding which processes may benefit from clearer controls or automation.',
    bullets: [
      'Stage 01: Identify spreadsheets, disconnected communication, and undocumented manual work.',
      'Stage 02: Review current workflows and the tools teams use.',
      'Stage 03: Practice connecting tools and coordinating agent-assisted work.',
      'Stage 04: Agree on improvements and ways to monitor the process.',
    ],
  },
  {
    id: 'safe-loop-governance',
    title: 'The Safe-Loop: 6-Stage Governance & Claims Hygiene',
    img: '/images/training/safe-loop-governance.svg',
    desc: 'A six-step review workflow for grounding, planning, making, and checking changes before release.',
    bullets: [
      'Step 01 Grounding: Review the project files and current state before planning.',
      'Steps 02 & 03 Planning & Checkpoint: Define the scope and get approval for destructive actions.',
      'Steps 04 & 05 Execution & Verification: Make the change and run checks that fit the project.',
      'Step 06 Review: Inspect the changes before merging them into the project.',
    ],
  },
];

// ─── ENGAGEMENT PATHWAYS ───
const engagementPathways = [
  { title: 'Operate the foundation', desc: 'Work through alert triage, exception queues, pipeline replay, and the runbooks for your delivered data products.', link: '/solutions', linkText: 'See the delivery plan ↗' },
  { title: 'Own the controls', desc: 'Understand the access model, source-to-target contracts, validation gates, and evidence used at acceptance.', link: '/solutions', linkText: 'Review the engagement ↗' },
  { title: 'Extend with review', desc: 'Practice the agreed process for schema changes, deployment checks, and adding a new source after handover.', link: '/contact#inquiry', linkText: 'Discuss your team’s readiness ↗' },
];

const Training = () => {
  const [activeModalDiagram, setActiveModalDiagram] = useState(null);

  return (
    <div className={`page ${styles.trainingPage}`}>
      <SEO
        title="Data Team Enablement & Handover"
        description="Operational handover for commercial data teams, with runbooks, quality controls, and supplementary AI engineering references."
        path="/training"
      />

      {/* ─── HERO HEADER ─── */}
      <header className={styles.heroHeader} id="top">
        <div className={`${styles.heroContent} visual-hero`}>
          <div>
            <h1 className={`${styles.heroHeadline} kinetic-title`}>Ready to run what ships.</h1>
            <p className={styles.heroLede}>Operational enablement is part of the commercial data foundation engagement. Your team practices the controls, exception handling, and release process it will own. The AI material below is a supplementary engineering reference library.</p>
            <p className="page-path">Concepts <span aria-hidden="true">→</span> Tools <span aria-hidden="true">→</span> Practice</p>
          </div>
          <SystemStory kind="learning" compact />
        </div>
      </header>

      <main>
        {/* ─── SECTION 6: HOW WE ENGAGE ─── */}
        <section className="section" id="engagement" aria-labelledby="engagement-heading">
          <div className="section__header">
            <h2 id="engagement-heading">Handover within the engagement</h2>
            <p>
              The final delivery stage equips your team to operate and extend the agreed data foundation. These are handover activities, not separate open-ended training packages.
            </p>
          </div>

          <div className={styles.engagementGrid}>
            {engagementPathways.map((item) => (
              <article key={item.title} className="card">
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
                <Link to={item.link} className="card__link">
                  {item.linkText}
                </Link>
              </article>
            ))}
          </div>
        </section>

        {/* ─── SECTION 1: CORE CONCEPTS & GLOSSARY (ZERO SLOP) ─── */}
        <section className="section" id="primer" aria-labelledby="primer-heading">
          <div className="section__header">
            <h2 id="primer-heading">Core AI concepts</h2>
            <p>
              Open a concept to see how it fits into the system.
            </p>
          </div>

          <div className={styles.glossaryGrid}>
            {glossaryTerms.map((t) => (
              <details key={t.title} className={styles.glossaryCard}>
                <summary><span className={styles.glossaryTitle}>{t.title}</span><span className="glossary-toggle" aria-hidden="true">+</span></summary>
                <p className={styles.glossaryDesc}>{t.desc}</p>
                <div className={styles.glossaryRelation} dangerouslySetInnerHTML={{ __html: t.relation }} />
              </details>
            ))}
          </div>
        </section>

        {/* ─── SECTION 2: PICK THE RIGHT TOOL + ECOSYSTEM ICONS ─── */}
        <section className="section" id="tools" aria-labelledby="tools-heading">
          <div className="section__header">
            <h2 id="tools-heading">Choose a tool for the task</h2>
            <p>
              For engineering teams, start in the editor or the terminal. We tailor each session to your team’s experience.
            </p>
          </div>

          <div className={styles.pathwayGrid}>
            {toolPaths.map((tp) => (
              <article key={tp.title} className={styles.pathwayCard}>
                <div
                  className={styles.pathwayMedia}
                  onClick={() =>
                    setActiveModalDiagram({
                      id: tp.title,
                      title: tp.title,
                      img: tp.img,
                      desc: tp.desc,
                      bullets: tp.bullets,
                    })
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveModalDiagram({
                        id: tp.title,
                        title: tp.title,
                        img: tp.img,
                        desc: tp.desc,
                        bullets: tp.bullets,
                      });
                    }
                  }}
                  aria-label={`Enlarge ${tp.title}`}
                >
                  <img src={tp.img} alt={tp.title} loading="lazy" />
                  <span className={styles.expandBadge}>Enlarge screenshot +</span>
                </div>
                <div className={styles.pathwayBody}>
                  <span className={styles.pathwayRole}>{tp.role}</span>
                  <h3 className={styles.pathwayTitle}>{tp.title}</h3>
                  <p className={styles.pathwayDesc}>{tp.desc}</p>
                  <details className="reading-details"><summary>What we practice</summary><ul>{tp.bullets.map(b => <li key={b}>{b}</li>)}</ul></details>
                  <div className={styles.pathwayFooter}>
                    <span className={styles.pathwayToolTag}>{tp.toolTag}</span>
                    <button
                      className={styles.pathwayInspectBtn}
                      onClick={() =>
                        setActiveModalDiagram({
                          id: tp.title,
                          title: tp.title,
                          img: tp.img,
                          desc: tp.desc,
                          bullets: tp.bullets,
                        })
                      }
                    >
                      Enlarge screenshot +
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Monochromatic AI Productivity Ecosystem Strip */}
          <div className={styles.ecosystemSection}>
            <div className={styles.ecosystemHeader}>
              <div className={styles.ecosystemTitle}>
                A toolkit matched to the work
              </div>
            </div>
            <div className={styles.ecosystemGrid}>
              {aiEcosystemTools.map((tool) => (
                <div key={tool.name} className={styles.ecosystemCard}>
                  <div className={styles.ecosystemIcon}>
                    <img src={tool.icon} alt={tool.name} loading="lazy" />
                  </div>
                  <div className={styles.ecosystemName}>{tool.name}</div>
                  <div className={styles.ecosystemRole}>{tool.role}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── SECTION 4: THE AI-NATIVE SPECTRUM ─── */}
        <section className="section" id="spectrum" aria-labelledby="spectrum-heading">
          <div className="section__header">
            <h2 id="spectrum-heading">The AI-Native Spectrum</h2>
            <p>
              Every team starts somewhere. Explore four stages and the next practical step for each.
            </p>
          </div>

          <MaturityExplorer />
        </section>

        {/* ─── SECTION 5: ARCHITECTURAL BLUEPRINTS ─── */}
        <section className="section" id="architectures" aria-labelledby="architecture-heading">
          <div className="section__header">
            <h2 id="architecture-heading">Technical references</h2>
            <p>
              An optional technical library for engineering teams. Inspect the models, memory, and
              governance behind the concepts above, with zoomable images and text notes.
            </p>
          </div>

          <details className="reference-library">
            <summary>Explore four reference blueprints</summary>
          <div className={styles.diagramGrid}>
            {architectureDiagrams.map((diag) => (
              <article key={diag.id} className={styles.diagramCard}>
                <div
                  className={styles.diagramBanner}
                  onClick={() => setActiveModalDiagram(diag)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveModalDiagram(diag);
                    }
                  }}
                  aria-label={`Enlarge ${diag.title}`}
                >
                  <img src={diag.img} alt={diag.title} loading="lazy" />
                  <span className={styles.expandBadge}>Enlarge blueprint +</span>
                </div>
                <div className={styles.diagramBody}>
                  <h3 className={styles.diagramTitle}>{diag.title}</h3>
                  <p className={styles.diagramDesc}>{diag.desc}</p>
                  <details className="reading-details"><summary>Inside this blueprint</summary><ul>{diag.bullets.map(b => <li key={b}>{b}</li>)}</ul></details>
                  <div className={styles.diagramFooter}>
                    <button
                      className={styles.diagramBtn}
                      onClick={() => setActiveModalDiagram(diag)}
                    >
                      Enlarge blueprint +
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
          </details>
        </section>

      </main>

      {/* ─── LIGHTBOX MODAL ─── */}
      {activeModalDiagram && (
        <MediaDialog
          className={styles.modalBackdrop}
          title={activeModalDiagram.title}
          onClose={() => setActiveModalDiagram(null)}
        >
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{activeModalDiagram.title}</h3>
              <button
                className={styles.modalCloseBtn}
                onClick={() => setActiveModalDiagram(null)}
              >
                Close (ESC) ✕
              </button>
            </div>
            <MediaViewport
              src={activeModalDiagram.img}
              alt={activeModalDiagram.title}
              description={activeModalDiagram.desc}
              bullets={activeModalDiagram.bullets || []}
            />
          </div>
        </MediaDialog>
      )}
    </div>
  );
};

export default Training;
